/**
 * useCallRecording Hook
 * Web-specific hook for recording audio using the MediaRecorder API.
 */

import { useState, useCallback, useRef, useEffect } from 'react'

export type AudioCaptureMode = 'microphone' | 'system'

interface UseCallRecordingReturn {
  isRecording: boolean
  isPaused: boolean
  recordingDuration: number
  audioLevel: number
  hasMicrophonePermission: boolean | null
  startRecording: (mode: AudioCaptureMode) => Promise<void>
  stopRecording: () => Promise<Blob | null>
  pauseRecording: () => void
  resumeRecording: () => void
  isMicrophoneSupported: boolean
  isSystemAudioSupported: boolean
  error: Error | null
}

export function useCallRecording(
  options: { maxDurationSeconds?: number; onError?: (error: Error) => void } = {}
): UseCallRecordingReturn {
  const { maxDurationSeconds = 3600, onError } = options

  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [audioLevel, setAudioLevel] = useState(0)
  const [hasMicrophonePermission, setHasMicrophonePermission] = useState<boolean | null>(null)
  const [error, setError] = useState<Error | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const isMicrophoneSupported =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia

  const isSystemAudioSupported =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getDisplayMedia

  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null }
    if (streamRef.current) { streamRef.current.getTracks().forEach((track) => track.stop()); streamRef.current = null }
    if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null }
    analyserRef.current = null
    mediaRecorderRef.current = null
  }

  useEffect(() => { return () => { cleanup() } }, [])

  useEffect(() => {
    if (isRecording && recordingDuration >= maxDurationSeconds) { stopRecording() }
  }, [recordingDuration, maxDurationSeconds, isRecording])

  const handleError = useCallback((err: Error) => {
    setError(err)
    onError?.(err)
    cleanup()
    setIsRecording(false)
    setIsPaused(false)
  }, [onError])

  const setupAudioAnalyser = (stream: MediaStream) => {
    try {
      const audioContext = new AudioContext()
      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      audioContextRef.current = audioContext
      analyserRef.current = analyser
      const dataArray = new Uint8Array(analyser.frequencyBinCount)
      const updateAudioLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray)
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length
          setAudioLevel(average / 255)
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel)
        }
      }
      updateAudioLevel()
    } catch (err) {
      console.warn('Could not setup audio analyser:', err)
    }
  }

  const startRecording = useCallback(async (mode: AudioCaptureMode): Promise<void> => {
    setError(null)
    audioChunksRef.current = []
    try {
      let stream: MediaStream
      if (mode === 'system') {
        if (!isSystemAudioSupported) throw new Error('System audio capture is not supported in this browser')
        const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } })
        const audioTracks = displayStream.getAudioTracks()
        if (audioTracks.length === 0) { displayStream.getTracks().forEach((track) => track.stop()); throw new Error('No audio track available.') }
        displayStream.getVideoTracks().forEach((track) => track.stop())
        stream = new MediaStream(audioTracks)
      } else {
        if (!isMicrophoneSupported) throw new Error('Microphone access is not supported in this browser')
        stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
      }
      streamRef.current = stream
      setHasMicrophonePermission(true)
      setupAudioAnalyser(stream)
      // Prefer mp4/aac — plays natively in Windows Media Player (Chrome 124+)
      const mimeType = MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2')
        ? 'audio/mp4;codecs=mp4a.40.2'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : 'audio/webm'
      const mediaRecorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 })
      mediaRecorder.ondataavailable = (event) => { if (event.data.size > 0) audioChunksRef.current.push(event.data) }
      mediaRecorder.onerror = (event) => { handleError(new Error('Recording error: ' + ((event as any).error?.message || 'Unknown error'))) }
      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start(1000)
      setIsRecording(true); setIsPaused(false); setRecordingDuration(0)
      timerRef.current = setInterval(() => { setRecordingDuration((prev) => prev + 1) }, 1000)
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setHasMicrophonePermission(false)
        handleError(new Error('Permission denied. Please allow access and try again.'))
      } else { handleError(error) }
    }
  }, [isMicrophoneSupported, isSystemAudioSupported, handleError])

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) { resolve(null); return }
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
      mediaRecorderRef.current.onstop = () => {
        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm'
        const blob = new Blob(audioChunksRef.current, { type: mimeType })
        cleanup(); setIsRecording(false); setIsPaused(false); setAudioLevel(0)
        resolve(blob)
      }
      if (mediaRecorderRef.current.state !== 'inactive') { mediaRecorderRef.current.stop() } else { resolve(null) }
    })
  }, [])

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause(); setIsPaused(true)
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    }
  }, [isRecording, isPaused])

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume(); setIsPaused(false)
      timerRef.current = setInterval(() => { setRecordingDuration((prev) => prev + 1) }, 1000)
    }
  }, [isRecording, isPaused])

  return { isRecording, isPaused, recordingDuration, audioLevel, hasMicrophonePermission, startRecording, stopRecording, pauseRecording, resumeRecording, isMicrophoneSupported, isSystemAudioSupported, error }
}

export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result as string
      const base64Data = base64.split(',')[1]
      resolve(base64Data)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export function getAudioFormat(mimeType: string): string {
  if (mimeType.includes('webm')) return 'webm'
  if (mimeType.includes('mp4')) return 'm4a'
  if (mimeType.includes('ogg')) return 'ogg'
  return 'webm'
}

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}
